// client/src/components/bank/BankProfile.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { User, Building, UserCheck, Bell, FileText } from 'lucide-react';

const BankProfile = () => {
   const { t, i18n } = useTranslation();
   const currentLanguage = i18n.language.startsWith('it') ? 'it' : 'en';
  const [user, setUser] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Add state for notification preferences
  const [emailLiquidityRequests, setEmailLiquidityRequests] = useState(true);
  const [emailBidApprovals, setEmailBidApprovals] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [preferredLanguage, setPreferredLanguage] = useState('it');
  
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await axios.get('/api/auth/profile');
        setUser(res.data.user);
        setOrganization(res.data.user.organization);
        setPreferredLanguage(res.data.user.preferredLanguage || 'it');
        
        // If user has notification preferences stored, use them
        if (res.data.user.emailPreferences) {
          setEmailLiquidityRequests(res.data.user.emailPreferences.liquidityRequests !== false);
          setEmailBidApprovals(res.data.user.emailPreferences.bidApprovals !== false);
        }
        
        setLoading(false);
      } catch (err) {
        setError(t('common.errors.fetchProfile'));
        setLoading(false);
      }
    };
    
    fetchProfile();
  }, [t]);
  
  // Save notification settings
  const handleSaveSettings = async (e) => {
    e.preventDefault();
    
    setSavingSettings(true);
    setSaveSuccess(false);
    
    try {
      // Update email preferences using the profile update endpoint
      await axios.put('/api/auth/profile', {
        preferredLanguage: preferredLanguage,
        emailPreferences: {
          liquidityRequests: emailLiquidityRequests,
          bidApprovals: emailBidApprovals
        }
      });
      
      // Show success message
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setError(t('common.errors.saveSettings'));
    } finally {
      setSavingSettings(false);
    }
  };
  
  if (loading) {
    return <div className="loading">{t('common.loadingProfile')}</div>;
  }
  
  if (error) {
    return <div className="error-message">{error}</div>;
  }
  
  return (
    <div className="company-dashboard">
      <div className="card">
        <div className="card-title">
          <User className="card-title-icon" size={20} />
          <span>{t('profile.activeUser')}</span>
        </div>
        <div style={{ padding: '20px 0', textAlign: 'left' }}>
          <h3 style={{ margin: '0 0 10px 0', color: 'var(--dark)' }}>
            {user?.firstName} {user?.lastName}
          </h3>
          <p style={{ margin: 0, color: 'var(--grey)' }}>
            {organization?.name} - {user?.position}
          </p>
        </div>
      </div>

            
      <div className="card company-dashboard">
        <div className="card-title">
          <UserCheck className="card-title-icon" size={20} />
          <span>{t('profile.userInformation')}</span>
        </div>
        <form id="officer-form">
          <div className="form-group">
            <label htmlFor="officer-name">{t('profile.name')}</label>
            <input type="text" id="officer-name" value={`${user?.firstName || ''} ${user?.lastName || ''}`} disabled />
          </div>
          <div className="form-group">
            <label htmlFor="officer-position">{t('profile.position')}</label>
            <input type="text" id="officer-position" value={user?.position || ''} disabled />
          </div>
          <div className="form-group">
            <label htmlFor="officer-email">{t('profile.email')}</label>
            <input type="email" id="officer-email" value={user?.email || ''} disabled />
          </div>
          <div className="form-group">
            <label htmlFor="officer-phone">{t('profile.phone')}</label>
            <input type="text" id="officer-phone" value={user?.phone || ''} disabled />
          </div>
        </form>
      </div>
      
      <div className="card company-dashboard">
        <div className="card-title">
          <Building className="card-title-icon" size={20} />
          <span>{t('profile.bankInformation')}</span>
        </div>
        <form id="profile-form">
          <div className="form-group">
            <label htmlFor="bank-name">{t('profile.bankName')}</label>
            <input type="text" id="bank-name" value={organization?.name || ''} disabled />
          </div>
          <div className="form-group">
            <label htmlFor="bank-id">{t('profile.bankId')}</label>
            <input type="text" id="bank-id" value={organization?.organizationId || ''} disabled />
          </div>
          <div className="form-group">
            <label htmlFor="bank-address">{t('profile.address')}</label>
            <input 
              type="text" 
              id="bank-address" 
              value={organization?.address ? 
                `${organization.address.street}, ${organization.address.city}, ${organization.address.postcode}, ${organization.address.country}` 
                : ''}
              disabled 
            />
          </div>
        </form>
      </div>

        <div className="card company-dashboard">
        <div className="card-title">
          <Bell className="card-title-icon" size={20} />
          <span>{t('profile.notificationSettings')}</span>
        </div>
        <form id="notification-form" onSubmit={handleSaveSettings}>
          <div className="form-group">
            <label htmlFor="preferredLanguage">{t('profile.preferredLanguage') || 'Preferred Language'}</label>
            <select
              id="preferredLanguage"
              value={preferredLanguage}
              onChange={(e) => setPreferredLanguage(e.target.value)}
              disabled={savingSettings}
              className="form-control"
            >
              <option value="it">Italiano</option>
              <option value="en">English</option>
            </select>
          </div>
          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input 
                type="checkbox" 
                checked={emailLiquidityRequests} 
                onChange={(e) => setEmailLiquidityRequests(e.target.checked)} 
              />
              <span>{t('profile.emailNotificationsRequests')}</span>
            </label>
          </div>
          
          {/*<div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input 
                type="checkbox" 
                checked={emailBidApprovals} 
                onChange={(e) => setEmailBidApprovals(e.target.checked)} 
              />
              <span>{t('profile.emailNotificationsBids')}</span>
            </label>
          </div>*/}
          
          <div className="form-actions" style={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: '15px',
              marginTop: '25px',
              paddingTop: '20px',
              borderTop: '1px solid var(--border)'
            }}>
              {saveSuccess && (
                <span className="success-message" style={{ color: 'var(--success)' }}>
                  {t('profile.settingsSaved')}
                </span>
              )}
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={savingSettings}
              >
                {savingSettings ? t('profile.saving') : t('profile.saveSettings')}
              </button>
            </div>
        </form>
      </div>

      <div className="card company-dashboard">
        <div className="card-title">
          <FileText className="card-title-icon" size={20} />
          <span>{t('profile.legalDocuments')}</span>
        </div>
        <div className="legal-documents">
          <p>{t('profile.reviewDocuments')}</p>
          <div className="document-links" style={{ marginTop: '15px' }}>
            <div className="document-link" style={{ marginBottom: '10px' }}>
              <a 
                href={currentLanguage === 'en' ? "/Termini e Condizioni di Utilizzo_eng.pdf" : "/Termini e Condizioni di Utilizzo_ita.pdf"}
                target="_blank" 
                rel="noopener noreferrer" 
                className="primary-link"
                style={{ textDecoration: 'none', color: 'var(--primary)', fontWeight: '500' }}
              >
                📄 {t('profile.termsConditions')}
              </a>
            </div>
            <div className="document-link" style={{ marginBottom: '10px' }}>
              <a 
                href={currentLanguage === 'en' ? "/Informativa Privacy_eng.pdf" : "/Informativa Privacy_ita.pdf"}
                target="_blank" 
                rel="noopener noreferrer" 
                className="primary-link"
                style={{ textDecoration: 'none', color: 'var(--primary)', fontWeight: '500' }}
              >
                🔒 {t('profile.privacyPolicy')}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BankProfile;